import React, { HTMLAttributes } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { observer } from "mobx-react";
import Profile from "assets/icons/44x44/profile.svg";
import ProfileBlack from "assets/icons/44x44/profile-black.svg";
import Home from "assets/icons/44x44/home.svg";
import HomeBlack from "assets/icons/44x44/home-black.svg";
import Catalog from "assets/icons/44x44/catalog.svg";
import CatalogBold from "assets/icons/44x44/catalog-bold.svg";
import Cheque from "assets/icons/32x32/cheque.svg";
import Cart from "assets/icons/44x44/cart.svg";
import CartBlack from "assets/icons/44x44/cart-black.svg";
import { useStore } from "store";

import { useBreakpoint, useUrl } from "helpers/hooks";
import { cn } from "helpers/style";
import { withTrailSlash } from "helpers/url";
import { ROUTE_CART, ROUTE_PROFILE } from "constants/routes";
import HelpModalIcon from "./components/HelpModalIcon/HelpModalIcon";

import styles from "./NavigationBar.module.scss";

const NavigationBar: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => {
  const router = useRouter();
  const { cart, user, header } = useStore();
  const { getLinkProps } = useUrl();
  const breakpoint = useBreakpoint(true);
  const needBar = ["mobile", "tablet"].includes(breakpoint);

  if (!needBar) return null;

  const onProfileClick = e => user.withAuth({ event: e });

  const onToggleCatalog = e => {
    e && e.preventDefault();

    if (header.catalogMobileOpen) {
      header.closeAllMenus();
      return;
    }

    header.openCatalogMobile();
  };

  const pathname = router?.pathname.split("/").filter(Boolean)[0];
  const isHomePage = router?.pathname === "/";
  const isCartPage = router?.pathname === ROUTE_CART;
  const isProfilePage = router?.pathname === ROUTE_PROFILE;
  const prescriptionsLength = user.emias?.prescriptions?.length;
  const isCatalogBtn =
    (pathname &&
      ["drugs", "brands", "diseases", "category"].includes(pathname)) ||
    header.catalogMobileOpen;

  return (
    <div className={cn([styles.navigationBar, className])} {...props}>
      <div className={styles.navigationBarContainer}>
        <div className={styles.navigationBarInner}>
          <Link
            {...getLinkProps("/")}
            className={cn([
              styles.navigationBarElement,
              isHomePage && styles.navigationBarElementActive,
            ])}
            passHref
          >
            {isHomePage && !isCatalogBtn ? <HomeBlack /> : <Home />}
            <span>Главная</span>
          </Link>

          {prescriptionsLength > 0 && (
            <Link
              {...getLinkProps(user.emias.url, undefined, {
                withRegion: false,
                withOrigin: true,
              })}
              className={styles.navigationBarElement}
              passHref
            >
              <Cheque />
              <span>Рецепты</span>
              <div className={styles.navigationBarCartCount}>
                {prescriptionsLength}
              </div>
            </Link>
          )}

          <div
            className={cn([
              styles.navigationBarElement,
              isCatalogBtn && styles.navigationBarElementActive,
            ])}
            onClick={onToggleCatalog}
          >
            {isCatalogBtn ? <CatalogBold /> : <Catalog />}
            <span>Каталог</span>
          </div>

          <a
            href={withTrailSlash(ROUTE_CART)}
            className={cn([
              styles.navigationBarElement,
              styles.navigationBarElementCart,
              isCartPage && styles.navigationBarElementActive,
            ])}
          >
            {isCartPage && !isCatalogBtn ? <CartBlack /> : <Cart />}{" "}
            <span>Корзина</span>
            <div className={styles.navigationBarCartCount}>{cart.count}</div>
          </a>

          <HelpModalIcon classNameChat={styles.navigationBarElement} />

          {user.isAuth ? (
            <Link
              {...getLinkProps(`/personal/${user.profile?.id}`, undefined, {
                withRegion: false,
                withOrigin: true,
              })}
              className={cn([
                styles.navigationBarElement,
                isProfilePage && styles.navigationBarElementActive,
              ])}
              onClick={onProfileClick}
            >
              <>
                {isProfilePage ? <ProfileBlack /> : <Profile />}
                <span>Профиль</span>
              </>
            </Link>
          ) : (
            <div
              className={styles.navigationBarElement}
              onClick={onProfileClick}
            >
              <Profile />
              <span>Профиль</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default observer(NavigationBar);
